import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Services, ServiceCategory, ServiceCategoryLabels, ServicesLabels } from '../../utils/enums';
import { documentsAPI, citiesAPI } from '../../services/api';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { Edit, Delete, Add, Description } from '@mui/icons-material';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

// Helper function to parse data payload from API
const parseDocumentsPayload = (data) => {
  if (!data) return [];
  let list = null;
  if (data.success === true && data.payload != null) {
    list = Array.isArray(data.payload) ? data.payload : [data.payload];
  } else if (data.success !== false && data.payload != null) {
    list = Array.isArray(data.payload) ? data.payload : [data.payload];
  } else if (Array.isArray(data)) {
    list = data;
  } else if (Array.isArray(data.payload)) {
    list = data.payload;
  } else if (data.payload != null) {
    list = [data.payload];
  }
  
  if (!list || list.length === 0) return [];
  
  return list.map(item => {
    let rawList = [];
    const docs = item.listOfDocuments ?? item.ListOfDocuments;
    if (docs != null) {
      rawList = Array.isArray(docs) ? docs : [docs];
    }
    
    const listOfDocuments = rawList.map(doc => {
      const title = (doc.title ?? doc.title_EN ?? doc.Title ?? doc.title_DE ?? '');
      const description = (doc.description ?? doc.description_EN ?? doc.Description ?? doc.description_DE ?? '');
      return {
        ...doc,
        title: title == null ? '' : String(title).trim(),
        description: description == null ? '' : String(description).trim(),
        title_EN: title == null ? '' : String(title).trim(),
        description_EN: description == null ? '' : String(description).trim()
      };
    });
    
    return {
      ...item,
      cityId: item.cityId ?? item.CityId,
      serviceId: item.serviceId ?? item.ServiceId,
      serviceCategoryTypeId: item.serviceCategoryTypeId ?? item.ServiceCategoryTypeId,
      listOfDocuments
    };
  });
};

const getDocumentRowKey = (document, doc) => {
  if (doc.clientKey) return String(doc.clientKey);
  if (doc.id != null && doc.id !== '') return `doc-id-${doc.id}`;
  const title = (doc.title ?? doc.title_EN ?? doc.Title ?? '').toString().trim();
  const description = (doc.description ?? doc.description_EN ?? doc.Description ?? '').toString().trim();
  return `${document.serviceId}-${document.serviceCategoryTypeId}-${title}-${description}`;
};

const getSubmitButtonLabel = (isSubmitting, editMode) => {
  if (isSubmitting) return 'Saving...';
  if (editMode) return 'Update';
  return 'Add';
};

const Documents = () => {
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [documents, setDocuments] = useState({ payload: [], totalRecord: 0 });
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCategoryType, setSelectedCategoryType] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    cityId: '',
    serviceId: '',
    serviceCategoryTypeId: '',
    serviceCategoryTypeName: '',
    listOfDocuments: []
  });
  const nextDocumentClientKeyRef = useRef(1);

  const allocateDocumentClientKey = () => {
    const key = `doc-${nextDocumentClientKeyRef.current}`;
    nextDocumentClientKeyRef.current += 1;
    return key;
  };

  const createEmptyDocumentItem = () => ({
    clientKey: allocateDocumentClientKey(),
    title_EN: '',
    description_EN: '',
    title: '',
    description: '',
  });

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true) {
        setCities(response.data.payload || []);
        // Set first city as default
        if (response.data.payload?.length > 0) {
          setSelectedCity(response.data.payload[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchDocuments = useCallback(async (cityId, serviceId, serviceCategoryTypeId) => {
    const cid = cityId ?? selectedCity;
    const sid = serviceId ?? selectedService;
    const scid = serviceCategoryTypeId ?? selectedCategory;
    setIsLoading(true);
    try {
      const response = await documentsAPI.getDocuments(
        cid || null,
        sid !== '' && sid != null ? Number(sid) : null,
        scid !== '' && scid != null ? Number(scid) : null
      );
      const payloadArray = parseDocumentsPayload(response?.data);

      setDocuments({
        payload: payloadArray,
        totalRecord: payloadArray.length
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments({ payload: [], totalRecord: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [selectedCity, selectedService, selectedCategory]);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    // Set first service as default on component mount
    const firstService = Object.values(Services)[0];
    if (firstService) {
      setSelectedService(firstService.toString());
      
      // Find matching categories for the selected service
      const matchingCategories = Object.entries(ServiceCategory)
        .filter(([key, value]) => {
          // For Student (1), show categories 1-5
          // For Resident (2), show categories 7 and 10
          if (firstService === Services.Student) {
            return value >= 1 && value <= 5;
          } else if (firstService === Services.Resident) {
            return value === 7 || value === 10;
          }
          return false;
        });

      // Set first matching category as default
      if (matchingCategories.length > 0) {
        const [, firstCategoryId] = matchingCategories[0];
        setSelectedCategory(firstCategoryId.toString());
      }
    }
  }, []); // Run only once on mount

  useEffect(() => {
    if (selectedService && selectedCategory) {
      fetchDocuments(selectedCity || null, selectedService, selectedCategory);
    } else {
      setDocuments({ payload: [], totalRecord: 0 });
    }
  }, [selectedCity, selectedService, selectedCategory, fetchDocuments]);

  useEffect(() => {
    const docsArray = Array.isArray(documents?.payload) ? documents.payload : [];
    const numCity = selectedCity ? Number(selectedCity) : null;
    const numService = selectedService ? Number(selectedService) : null;
    const numCategory = selectedCategory ? Number(selectedCategory) : null;

    let filtered = docsArray;
    if (numCity != null) {
      filtered = filtered.filter(doc => Number(doc.cityId) === numCity || Number(doc.cityId) === 0);
    }
    if (numService != null) {
      filtered = filtered.filter(doc => Number(doc.serviceId) === numService);
    }
    if (numCategory != null) {
      filtered = filtered.filter(doc => Number(doc.serviceCategoryTypeId) === numCategory);
    }

    filtered = filtered.map(doc => ({
      ...doc,
      listOfDocuments: Array.isArray(doc.listOfDocuments) ? doc.listOfDocuments : []
    }));

    setFilteredDocuments(filtered);
  }, [documents?.payload, selectedCity, selectedService, selectedCategory]);

  const handleOpen = (document = null) => {
    if (document) {
      const listOfDocuments = (document.listOfDocuments || []).map((d) => ({
        ...d,
        clientKey: d.clientKey || (d.id != null ? `doc-id-${d.id}` : allocateDocumentClientKey()),
        title_EN: d.title_EN ?? d.title ?? '',
        description_EN: d.description_EN ?? d.description ?? ''
      }));
      const docCityId = document.cityId ?? document.CityId;
      const cityIdForForm = (docCityId !== 0 && docCityId !== '' && docCityId != null)
        ? String(docCityId)
        : (selectedCity || '');
      setCurrentDocument({
        ...document,
        cityId: cityIdForForm,
        serviceId: (document.serviceId ?? document.ServiceId ?? '').toString(),
        serviceCategoryTypeId: (document.serviceCategoryTypeId ?? document.ServiceCategoryTypeId ?? '').toString(),
        listOfDocuments
      });
      setEditMode(true);
    } else {
      setCurrentDocument({
        cityId: selectedCity,
        serviceId: selectedService,
        serviceCategoryTypeId: selectedCategory,
        serviceCategoryTypeName: selectedCategoryType,
        listOfDocuments: [createEmptyDocumentItem()]
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const getCategoriesForService = (serviceId) => {
    const sid = Number(serviceId);
    if (sid === Services.Student) {
      return Object.entries(ServiceCategory).filter(([, value]) => value >= 1 && value <= 5);
    }
    if (sid === Services.Resident) {
      return Object.entries(ServiceCategory).filter(([, value]) => value === 7 || value === 10);
    }
    return [];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'serviceId') {
      const categories = getCategoriesForService(value);
      const firstCategoryId = categories.length > 0 ? categories[0][1] : '';
      const firstCategoryName = firstCategoryId ? ServiceCategoryLabels[firstCategoryId] : '';
      setCurrentDocument({
        ...currentDocument,
        serviceId: String(value),
        serviceCategoryTypeId: String(firstCategoryId),
        serviceCategoryTypeName: firstCategoryName
      });
    } else {
      setCurrentDocument({
        ...currentDocument,
        [name]: value
      });
    }
  };

  const handleSubmit = async () => {
    const list = currentDocument.listOfDocuments || [];
    const hasEmpty = list.some(
      (d) => !(d.title_EN || d.title || '').trim() || !(d.description_EN || d.description || '').trim()
    );
    if (hasEmpty) {
      alert('Please fill in Title and Description for all documents.');
      return;
    }

    const listOfDocuments = list.map((d) => ({
      title: (d.title_EN ?? d.title ?? '').toString().trim(),
      description: (d.description_EN ?? d.description ?? '').toString().trim()
    }));

    const payload = {
      cityId: currentDocument.cityId === '' || currentDocument.cityId == null ? 0 : Number(currentDocument.cityId),
      serviceId: Number(currentDocument.serviceId) || 0,
      serviceCategoryTypeId: Number(currentDocument.serviceCategoryTypeId) || 0,
      serviceCategoryTypeName: currentDocument.serviceCategoryTypeName || '',
      listOfDocuments
    };

    setIsSubmitting(true);
    try {
      await documentsAPI.createDocument(payload);
      handleClose();
      setSelectedService(String(payload.serviceId));
      setSelectedCategory(String(payload.serviceCategoryTypeId));
      fetchDocuments(selectedCity, payload.serviceId, payload.serviceCategoryTypeId);
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Error saving document: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (serviceId, serviceCategoryTypeId) => {
    if (!browserGlobal.confirm('Are you sure you want to delete this document?')) return;
    
    setIsSubmitting(true);
    try {
      await documentsAPI.deleteDocument(serviceId, serviceCategoryTypeId);
      fetchDocuments(selectedCity, selectedService, selectedCategory);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDocument = () => {
    setCurrentDocument({
      ...currentDocument,
      listOfDocuments: [
        ...currentDocument.listOfDocuments,
        createEmptyDocumentItem(),
      ]
    });
  };

  const handleDocumentChange = (e, index) => {
    const { name, value } = e.target;
    const updatedDocuments = [...currentDocument.listOfDocuments];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      [name]: value
    };
    setCurrentDocument({
      ...currentDocument,
      listOfDocuments: updatedDocuments
    });
  };

  const handleDocumentDelete = (index) => {
    const updatedDocuments = [...currentDocument.listOfDocuments];
    updatedDocuments.splice(index, 1);
    setCurrentDocument({
      ...currentDocument,
      listOfDocuments: updatedDocuments
    });
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (filteredDocuments.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No documents found</Typography>
        </Box>
      );
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredDocuments.flatMap(document =>
            (document.listOfDocuments || []).map((doc) => (
              <TableRow key={getDocumentRowKey(document, doc)}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description fontSize="small" color="primary" />
                    <Typography variant="subtitle2">
                      {(doc.title ?? doc.title_EN ?? doc.Title ?? '') || '—'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {(doc.description ?? doc.description_EN ?? doc.Description ?? '') || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <IconButton
                      onClick={() => handleOpen(document)}
                      size="small"
                      sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                      title="Edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    {isSuperAdmin && (
                      <IconButton
                        onClick={() => handleDelete(document.serviceId, document.serviceCategoryTypeId)}
                        size="small"
                        sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}
                        title="Delete"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" color="primary">Documents</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            Add Documents
          </Button>
        )}
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={selectedCity}
                label="City"
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={selectedService}
                label="Service Type"
                onChange={(e) => {
                  const newService = e.target.value;
                  setSelectedService(newService);
                  
                  // Reset and update category based on selected service
                  const matchingCategories = Object.entries(ServiceCategory)
                    .filter(([key, value]) => {
                      if (Number.parseInt(newService, 10) === Services.Student) {
                        return value >= 1 && value <= 5;
                      } else if (Number.parseInt(newService, 10) === Services.Resident) {
                        return value === 7 || value === 10;
                      }
                      return false;
                    });

                  if (matchingCategories.length > 0) {
                    const [, firstCategoryId] = matchingCategories[0];
                    setSelectedCategory(firstCategoryId.toString());
                  } else {
                    setSelectedCategory('');
                  }
                }}
              >
                {Object.entries(Services).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {ServicesLabels[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Service Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Service Category"
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedCategoryType(ServiceCategoryLabels[e.target.value] || '');
                }}
              >
                {Object.entries(ServiceCategory)
                  .filter(([key, value]) => {
                    if (Number.parseInt(selectedService, 10) === Services.Student) {
                      return value >= 1 && value <= 5;
                    } else if (Number.parseInt(selectedService, 10) === Services.Resident) {
                      return value === 7 || value === 10;
                    }
                    return false;
                  })
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {ServiceCategoryLabels[value]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box component={Paper} sx={{ overflowX: 'auto' }}>
        {renderTableContent()}
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Documents' : 'Add New Documents'}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select
                    name="cityId"
                    value={currentDocument.cityId ?? ''}
                    onChange={handleChange}
                    label="City"
                  >
                    {cities.map(city => (
                      <MenuItem key={city.id} value={String(city.id)}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Service</InputLabel>
                  <Select
                    name="serviceId"
                    value={currentDocument.serviceId ?? ''}
                    onChange={handleChange}
                    label="Service Type"
                  >
                    {Object.entries(Services).map(([key, value]) => (
                      <MenuItem key={value} value={String(value)}>
                        {ServicesLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Service Category</InputLabel>
                  <Select
                    name="serviceCategoryTypeId"
                    value={currentDocument.serviceCategoryTypeId ?? ''}
                    label="Service Category"
                    onChange={(e) => {
                      const val = e.target.value;
                      setCurrentDocument(prev => ({
                        ...prev,
                        serviceCategoryTypeId: val,
                        serviceCategoryTypeName: ServiceCategoryLabels[Number(val)] || ''
                      }));
                    }}
                  >
                    {getCategoriesForService(currentDocument.serviceId).map(([key, value]) => (
                      <MenuItem key={value} value={String(value)}>
                        {ServiceCategoryLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
             
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="h6">Required Documents</Typography>
                </Box>
                <Grid container spacing={2}>
                  {currentDocument.listOfDocuments.map((doc, index) => (
                    <React.Fragment key={doc.clientKey || getDocumentRowKey(currentDocument, doc)}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Title"
                          name="title_EN"
                          value={doc.title_EN || doc.title || ''}
                          onChange={(e) => handleDocumentChange(e, index)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description"
                          name="description_EN"
                          value={doc.description_EN || doc.description || ''}
                          onChange={(e) => handleDocumentChange(e, index)}
                          multiline
                          rows={3}
                        />
                      </Grid>
                      {index > 0 && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="flex-end">
                            <IconButton
                              onClick={() => handleDocumentDelete(index)}
                              sx={{ color: 'error.main' }}
                              size="small"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      )}
                      {index < currentDocument.listOfDocuments.length - 1 && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                      )}
                    </React.Fragment>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddDocument}
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Add Another Document
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)'
              }
            }}
          >
            {getSubmitButtonLabel(isSubmitting, editMode)}
          </Button>
        </DialogActions>
      </Dialog>

      
    </Box>
  );
};

export default Documents;
